"use client";

import { useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { Newspaper, ZoomIn } from "lucide-react";
import type { PricePoint } from "@/lib/data/types";
import type { NewsPriceMarker } from "@/lib/news-price-correlation";
import { cn } from "@/lib/utils";
import { RangeSelector, type ChartRangeLabel } from "@/components/charts/range-selector";
import {
  compute30DayVolatility,
  computePriceAxisTicks,
  downsample,
  filterDateRange,
  formatPriceAxisTick,
  pricePrecisionDigits,
  withTimeField,
} from "@/lib/price-chart-utils";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";
import { useDragZoom } from "@/lib/use-drag-zoom";

const MIN_SPAN_MS = 3 * 24 * 60 * 60 * 1000; // 3 Tage - verhindert Zoom in einen unbrauchbaren Splitter.

function formatTooltipDate(value: string | number): string {
  return new Date(value).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const changeFormatter = new Intl.NumberFormat("de-DE", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "always",
});

/**
 * Kursverlauf-Chart (Erik 2026-08-20: "wirklich nur der Kurs dargestellt wird wie in
 * der früheren Version... nur den Chart inkl. Volumen") - der zwischenzeitlich
 * ergänzte Zwei-Punkte-Rendite-Modus ([Punkt 40], Vorbild Google Finance) ist auf
 * Eriks ausdrücklichen Wunsch wieder vollständig entfernt, keine "Rendite
 * berechnen"-Umschaltung mehr. Bleibt bei reinem Klick-Zieh-/Mausrad-Zoom (siehe
 * lib/use-drag-zoom.ts) plus Kursfläche + Volumen-Balken + Ex-Dividenden-/News-Marker.
 */
export function PriceHistoryChart({
  data,
  currency = "USD",
  purchasePrice,
  exDividendDates = [],
  newsMarkers = [],
}: {
  data: PricePoint[];
  currency?: string;
  /** Eigener Ø-Kaufpreis aus dem Depot, falls die Aktie gehalten wird (backlog.md ->
   * "Eigenen Kaufkurs als Marker im Kurschart einzeichnen") - undefined fuer Gaeste,
   * nicht gehaltene Aktien, oder eine abweichende Kaufwaehrung (siehe
   * price-chart-section.tsx). */
  purchasePrice?: number;
  /** Ex-Dividenden-Tage der letzten ~2 Jahre als ISO-Datums-Strings (backlog.md ->
   * "Dividenden-Ex-Tage als kleine Marker im Kurschart, damit der erwartete
   * Kursrückgang nicht verwirrt") - leeres Array fuer Aktien ohne Dividende. */
  exDividendDates?: string[];
  /** Auffällige Kursausschläge mit zeitlich passender News (backlog.md -> "Preis-Chart
   * mit Nachrichten-Markierungen verknüpfen") - typischerweise 0-2 Einträge, da `news`
   * nur die ~8 aktuellsten Meldungen umfasst (siehe lib/news-price-correlation.ts). */
  newsMarkers?: NewsPriceMarker[];
}) {
  const [range, setRange] = useState<ChartRangeLabel>("Max");

  const priceDigits = useMemo(
    () => pricePrecisionDigits(data[data.length - 1]?.close),
    [data],
  );

  // Kurzfristige Volatilitaets-Kennzahl (backlog.md -> "Kurzfristige Volatilitaets-
  // Kennzahl") - bewusst aus `data` (voller Verlauf), nicht `filtered`/`rendered`: "wie
  // stark schwankt die Aktie GERADE" soll sich nicht aendern, nur weil der Nutzer den
  // Chart-Zeitraum auf "Max" umstellt.
  const volatility30d = useMemo(() => compute30DayVolatility(data), [data]);

  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency,
        minimumFractionDigits: priceDigits,
        maximumFractionDigits: priceDigits,
      }),
    [currency, priceDigits],
  );

  const filtered = useMemo(() => filterDateRange(data, range), [data, range]);

  // Nur fuers Rendering ausduennen (Performance) - first/last/change unten nutzen
  // weiterhin `filtered` fuer eine exakte Kursaenderung ueber die gesamte Periode.
  const rendered = useMemo(
    () => withTimeField(downsample(filtered, 1000, (p) => p.close)),
    [filtered],
  );

  // Klick+Ziehen zoomt zusaetzlich zum 1J/3J/5J/10J/Max-Range-Selector in einen frei
  // waehlbaren Ausschnitt (Erik 2026-07-11, Feedback-Runde 8 Punkt 4), Mausrad-Zoom
  // (backlog.md Punkt 61, Vorbild TradingView) zoomt symmetrisch um die Mitte des aktuell
  // sichtbaren Bereichs, Tastatur-Alternative (Shift+Pfeiltasten+Enter) fuer beide -
  // gemeinsamer Hook seit der Siebzehnten autonomen Charge, siehe dessen Docstring
  // (lib/use-drag-zoom.ts) fuer die volle Begruendung/Funktionsweise.
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: rendered[0]?.time,
    fullMax: rendered[rendered.length - 1]?.time,
    minSpan: MIN_SPAN_MS,
  });

  const visibleRendered = useMemo(() => {
    if (!zoomRange) return rendered;
    const [lo, hi] = zoomRange;
    return rendered.filter((p) => p.time >= lo && p.time <= hi);
  }, [rendered, zoomRange]);

  // Nur die Marker im aktuell sichtbaren Zeitfenster zeichnen (RangeSelector + Zoom) -
  // ausserhalb liegende Termine wuerden ohnehin nicht angezeigt und Recharts unnoetig
  // belasten.
  const visibleExDividendTimes = useMemo(() => {
    if (exDividendDates.length === 0 || visibleRendered.length === 0) return [];
    const lo = visibleRendered[0].time;
    const hi = visibleRendered[visibleRendered.length - 1].time;
    return exDividendDates.map((d) => Date.parse(d)).filter((t) => t >= lo && t <= hi);
  }, [exDividendDates, visibleRendered]);

  const visibleNewsMarkers = useMemo(() => {
    if (newsMarkers.length === 0 || visibleRendered.length === 0) return [];
    const lo = visibleRendered[0].time;
    const hi = visibleRendered[visibleRendered.length - 1].time;
    return newsMarkers
      .map((m) => ({ ...m, time: Date.parse(m.date) }))
      .filter((m) => m.time >= lo && m.time <= hi);
  }, [newsMarkers, visibleRendered]);

  const isNarrow = useIsNarrowViewport();
  const axisTicks = useMemo(
    () => computePriceAxisTicks(visibleRendered, range, isNarrow ? 5 : 8).map((d) => Date.parse(d)),
    [visibleRendered, range, isNarrow],
  );

  const maxVolume = useMemo(
    () => visibleRendered.reduce((max, p) => (p.volume && p.volume > max ? p.volume : max), 0),
    [visibleRendered],
  );
  const hasVolume = maxVolume > 0;

  const volumeFormatter = useMemo(() => new Intl.NumberFormat("de-DE", { notation: "compact" }), []);

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Kursverlauf aktuell nicht verfügbar.</p>
    );
  }

  const first = filtered[0];
  const last = filtered[filtered.length - 1];
  const change = first && last && first.close > 0 ? (last.close - first.close) / first.close : undefined;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-serif text-3xl text-ink">{priceFormatter.format(last.close)}</div>
          {change !== undefined && (
            <div className={cn("font-tabular text-sm", change >= 0 ? "text-positive" : "text-negative")}>
              {changeFormatter.format(change)}
            </div>
          )}
          {volatility30d !== undefined && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              Ø Tagesschwankung (30 Tage): {changeFormatter.format(volatility30d).replace("+", "±")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {zoomRange ? (
            <button
              type="button"
              onClick={resetZoom}
              className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
            >
              Zoom zurücksetzen
            </button>
          ) : (
            <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex">
              <ZoomIn className="h-3 w-3" aria-hidden="true" />
              Bereich ziehen oder Mausrad zum Zoomen
            </span>
          )}
          <RangeSelector
            value={range}
            onChange={(r) => {
              setRange(r);
              resetZoom();
            }}
          />
        </div>
      </div>

      <div
        ref={chartWrapperRef}
        className="h-96 w-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onKeyDown={onKeyDown}
        {...keyboardA11yProps}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={visibleRendered}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
          >
            <defs>
              <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16265C" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#16265C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
              ticks={axisTicks}
              interval={0}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              yAxisId="price"
              domain={["auto", "auto"]}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => priceFormatter.format(v)}
              width={70}
              axisLine={false}
              tickLine={false}
            />
            {hasVolume && (
              <YAxis
                yAxisId="volume"
                domain={[0, maxVolume * 4]}
                hide
              />
            )}
            <Tooltip
              formatter={(value, name) =>
                name === "volume"
                  ? [volumeFormatter.format(Number(value)), "Volumen"]
                  : [priceFormatter.format(Number(value)), "Kurs"]
              }
              labelFormatter={(label) => formatTooltipDate(Number(label))}
              cursor={{ stroke: "#16265C", strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
              contentStyle={{
                borderRadius: 4,
                borderColor: "#ECEAE3",
                fontSize: 13,
                boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
              }}
            />
            {hasVolume && (
              <Bar
                yAxisId="volume"
                dataKey="volume"
                fill="#16265C"
                fillOpacity={0.12}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            )}
            {visibleExDividendTimes.map((t) => (
              <ReferenceLine
                key={t}
                yAxisId="price"
                x={t}
                stroke="#C6A15B"
                strokeWidth={1}
                strokeOpacity={0.45}
                strokeDasharray="2 3"
              />
            ))}
            {visibleNewsMarkers.map((m) => (
              <ReferenceLine
                key={m.time}
                yAxisId="price"
                x={m.time}
                stroke={m.changePercent >= 0 ? "#3F7A5B" : "#B4544C"}
                strokeWidth={1.5}
                strokeOpacity={0.5}
              />
            ))}
            <Area
              yAxisId="price"
              type="linear"
              dataKey="close"
              stroke="#16265C"
              strokeWidth={1.75}
              fill="url(#priceFill)"
              dot={false}
              activeDot={{ r: 4, fill: "#16265C", stroke: "#FAF9F5", strokeWidth: 2 }}
              isAnimationActive={false}
            />
            {purchasePrice !== undefined && (
              <ReferenceLine
                yAxisId="price"
                y={purchasePrice}
                stroke="#3F7A5B"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                label={{
                  value: `Ø Kaufpreis: ${priceFormatter.format(purchasePrice)}`,
                  position: "insideBottomLeft",
                  fill: "#3F7A5B",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            )}
            {refAreaLeft !== null && refAreaRight !== null && (
              <ReferenceArea
                yAxisId="price"
                x1={refAreaLeft}
                x2={refAreaRight}
                fill="#16265C"
                fillOpacity={0.12}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {visibleExDividendTimes.length > 0 && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2.5 w-0 border-l-2 border-dashed border-[#C6A15B]" aria-hidden="true" />
          Ex-Dividenden-Tag (kann zu einem kleinen, erwarteten Kursrückgang führen)
        </p>
      )}
      {visibleNewsMarkers.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {visibleNewsMarkers.map((m) => (
            <a
              key={m.time}
              href={m.news.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-1.5 text-xs text-muted-foreground hover:text-ink"
            >
              <Newspaper
                className={cn("mt-0.5 h-3 w-3 shrink-0", m.changePercent >= 0 ? "text-positive" : "text-negative")}
                aria-hidden="true"
              />
              <span>
                <span className="font-medium text-ink-secondary">{new Date(m.date).toLocaleDateString("de-DE")}</span>
                {" · "}
                <span className={m.changePercent >= 0 ? "text-positive" : "text-negative"}>
                  {changeFormatter.format(m.changePercent)}
                </span>
                {" · "}
                {m.news.title}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
